import { useCallback, useRef } from 'react';

/**
 * Returns pointer-event handlers that fire `action` immediately on press,
 * then repeatedly while the button is held down.
 *
 * @param action      Callback to repeat
 * @param delay       First repeat delay in ms (default 350)
 * @param interval    Repeat interval in ms (default 120)
 */
export function useHoldRepeat(
  action: () => void,
  delay = 350,
  interval = 120,
) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);

  const stop = useCallback(() => {
    if (timeoutRef.current)  { clearTimeout(timeoutRef.current);   timeoutRef.current  = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  const start = useCallback(() => {
    stop();
    action();                                           // fire immediately
    timeoutRef.current = setTimeout(() => {             // then start repeating
      intervalRef.current = setInterval(action, interval);
    }, delay);
  }, [action, delay, interval, stop]);

  return {
    onPointerDown : (e: React.PointerEvent) => { e.preventDefault(); start(); },
    onPointerUp   : stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    // prevent context menu on long-press on mobile
    onContextMenu : (e: React.MouseEvent) => e.preventDefault(),
  };
}
